import { useState, useEffect } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Container,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { updateUser, saveCreateForm } from "../axios/axiosFunctions";
import { hasProfileFormChanged } from "../utils/user";

const DisplayProfileForm = ({ userInfo, userId, setUserInfo }) => {
  const [profileForm, setProfileForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    industry: "",
    howLongCompany: "",
    functionCompany: [
      false,
      false,
      false,
      false,
      false,
      false,
      false,
      false,
      false,
      false,
      false,
    ],
    managementPosition: "",
    titleCompany: "",
    directors: "",
    yearRange: "",
    gender: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({ success: false, msg: "" });

  const {
    firstName,
    lastName,
    email,
    company,
    industry,
    howLongCompany,
    functionCompany,
    managementPosition,
    titleCompany,
    directors,
    yearRange,
    gender,
  } = profileForm;

  useEffect(() => {
    if (userInfo) {
      setProfileForm({
        firstName: userInfo.firstName || "",
        lastName: userInfo.lastName || "",
        email: userInfo.email || "",
        company: userInfo.info?.company || "",
        industry: userInfo.info?.industry || "",
        howLongCompany: userInfo.info?.howLongCompany || "",
        functionCompany: userInfo.info?.functionCompany || [
          false,
          false,
          false,
          false,
          false,
          false,
          false,
          false,
          false,
          false,
          false,
        ],
        managementPosition: userInfo.info?.managementPosition || "",
        titleCompany: userInfo.info?.titleCompany || "",
        directors: userInfo.info?.directors || "",
        yearRange: userInfo.info?.yearRange || "",
        gender: userInfo.info?.gender || "",
      });
    }
  }, [userInfo]);

  const handleProfileForm = (e) => {
    const { name, value } = e.target;
    setProfileForm({ ...profileForm, [name]: value });
  };

  const handleFunctionRadio = ({ target }) => {
    const { value } = target;
    const newfunctionCompany = functionCompany.map(() => false);
    newfunctionCompany[parseInt(value)] = true;
    setProfileForm({ ...profileForm, functionCompany: newfunctionCompany });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const userData = {
      firstName,
      lastName,
      email,
    };
    const resUser = await updateUser(userId, userData);

    if (!resUser.user) {
      setLoading(false);
      setError({
        success: false,
        msg: resUser.msg || "Error updating user information",
      });
      return;
    }
    // Update profile info
    const profileData = {
      company,
      industry,
      howLongCompany,
      functionCompany,
      managementPosition,
      titleCompany,
      directors,
      yearRange,
      gender,
    };

    const resProfile = await saveCreateForm(userId, profileData);

    if (!resProfile.status) {
      setLoading(false);
      setError({
        success: false,
        msg: resProfile.msg || "Error updating profile information",
      });
      return;
    }

    setError({ success: true, msg: "Profile updated successfully!" });
    setUserInfo({
      ...userInfo,
      firstName,
      lastName,
      email,
      info: {
        ...userInfo?.info,
        company,
        industry,
        howLongCompany,
        functionCompany,
        managementPosition,
        titleCompany,
        directors,
        yearRange,
        gender,
      },
    });
    setLoading(false);
  };

  return (
    <Container sx={{ marginTop: "50px" }}>
      <form onSubmit={handleSubmit}>
        <Grid
          size={12}
          sx={{ display: "flex", justifyContent: "end", marginBottom: "10px" }}
        >
          <Button
            type="submit"
            size="large"
            variant="contained"
            color="secondary"
            disabled={loading || !hasProfileFormChanged(userInfo, profileForm)}
          >
            {loading ? "SAVING..." : "SAVE CHANGES"}
          </Button>
        </Grid>
        <Grid container spacing={3} sx={{ marginBottom: "30px" }}>
          <Grid size={6}>
            <Typography variant="h6" color="secondary">
              First Name
            </Typography>
            <TextField
              fullWidth
              value={firstName}
              onChange={handleProfileForm}
              name="firstName"
              required
            />
          </Grid>
          <Grid size={6}>
            <Typography variant="h6" color="secondary">
              Last Name
            </Typography>
            <TextField
              fullWidth
              value={lastName}
              onChange={handleProfileForm}
              name="lastName"
              required
            />
          </Grid>
          <Grid size={12}>
            <Typography variant="h6" color="secondary">
              Email
            </Typography>
            <TextField
              fullWidth
              value={email}
              onChange={handleProfileForm}
              name="email"
              required
              type="email"
            />
          </Grid>
          <Grid size={12}>
            <Typography variant="h6" color="secondary">
              Company Name
            </Typography>
            <TextField
              fullWidth
              value={company}
              onChange={handleProfileForm}
              name="company"
            />
          </Grid>
        </Grid>
        <Accordion
          sx={{
            width: "100%",
            border: "2px solid",
            borderColor: "primary.main",
          }}
        >
          <AccordionSummary expandIcon={<ArrowDropDownIcon color="primary" />}>
            <Typography variant="h5" fontWeight={600} color="primary">
              Profile Information
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid size={12}>
                <Typography variant="h6">
                  How long have you been at your current company?
                </Typography>
                <RadioGroup
                  value={howLongCompany}
                  onChange={handleProfileForm}
                  name="howLongCompany"
                  row
                  fullWidth
                  sx={{ justifyContent: "space-between", padding: "0px 10px" }}
                >
                  <FormControlLabel
                    value="less6"
                    control={<Radio />}
                    label="Less than 6 months"
                  />
                  <FormControlLabel
                    value="6to1"
                    control={<Radio />}
                    label="6 months to 1 year"
                  />
                  <FormControlLabel
                    value="1to3"
                    control={<Radio />}
                    label="Between 1 and 3 years"
                  />
                  <FormControlLabel
                    value="more3"
                    control={<Radio />}
                    label="More than 3 years"
                  />
                </RadioGroup>
              </Grid>
              <Grid size={12}>
                <Typography variant="h6">
                  In which Function do you work?{" "}
                </Typography>
                <RadioGroup row value={String(functionCompany.indexOf(true))} onChange={handleFunctionRadio}>
                  <FormControlLabel
                    sx={{ margin: { xs: "0px" } }}
                    control={<Radio />}
                    value="0"
                    label="C-Suite"
                  />
                  <FormControlLabel
                    sx={{ margin: { xs: "0px" } }}
                    control={<Radio />}
                    value="1"
                    label="IT"
                  />
                  <FormControlLabel
                    sx={{ margin: { xs: "0px" } }}
                    control={<Radio />}
                    value="2"
                    label="F&A"
                  />
                  <FormControlLabel
                    sx={{ margin: { xs: "0px" } }}
                    control={<Radio />}
                    value="3"
                    label="Operations"
                  />
                  <FormControlLabel
                    sx={{ margin: { xs: "0px" } }}
                    control={<Radio />}
                    value="4"
                    label="HR"
                  />
                  <FormControlLabel
                    sx={{ margin: { xs: "0px" } }}
                    control={<Radio />}
                    value="5"
                    label="Legal"
                  />
                  <FormControlLabel
                    sx={{ margin: { xs: "0px" } }}
                    control={<Radio />}
                    value="6"
                    label="Customer Services"
                  />
                  <FormControlLabel
                    sx={{ margin: { xs: "0px" } }}
                    control={<Radio />}
                    value="7"
                    label="Sales & Marketing"
                  />
                  <FormControlLabel
                    sx={{ margin: { xs: "0px" } }}
                    control={<Radio />}
                    value="8"
                    label="Manufacturing"
                  />
                  <FormControlLabel
                    sx={{ margin: { xs: "0px" } }}
                    control={<Radio />}
                    value="9"
                    label="R&D"
                  />
                  <FormControlLabel
                    sx={{ margin: { xs: "0px" } }}
                    control={<Radio />}
                    value="10"
                    label="Other"
                  />
                </RadioGroup>
              </Grid>
              <Grid size={12}>
                <Typography variant="h6">
                  What type of management position do you hold?{" "}
                </Typography>
                <RadioGroup
                  value={managementPosition}
                  onChange={handleProfileForm}
                  name="managementPosition"
                  row
                  fullWidth
                  sx={{ justifyContent: "space-between", padding: "0px 10px" }}
                >
                  <FormControlLabel
                    value="DMO"
                    control={<Radio />}
                    label="Direct Manager of Others"
                  />
                  <FormControlLabel
                    value="MM"
                    control={<Radio />}
                    label="Manager of Managers"
                  />
                  <FormControlLabel
                    value="MMDR"
                    control={<Radio />}
                    label="Matrix Manager without Direct Reports"
                  />
                  <FormControlLabel
                    value="NMY"
                    control={<Radio />}
                    label="Not a Manager Yet"
                  />
                  <FormControlLabel
                    value="other"
                    control={<Radio />}
                    label="Other"
                  />
                </RadioGroup>
              </Grid>
              <Grid size={12}>
                <Typography variant="h6">What is your title?</Typography>
                <RadioGroup
                  value={titleCompany}
                  onChange={handleProfileForm}
                  name="titleCompany"
                  row
                  fullWidth
                  sx={{ justifyContent: "space-between", padding: "0px 10px" }}
                >
                  <FormControlLabel
                    value="supervisor"
                    control={<Radio />}
                    label="Supervisor"
                  />
                  <FormControlLabel
                    value="manager"
                    control={<Radio />}
                    label="Manager/Sr. Manager"
                  />
                  <FormControlLabel
                    value="director"
                    control={<Radio />}
                    label="Director (All Levels)"
                  />
                  <FormControlLabel
                    value="vp"
                    control={<Radio />}
                    label="VP (All Levels)"
                  />
                  <FormControlLabel
                    value="csuite"
                    control={<Radio />}
                    label="C-Suite"
                  />
                  <FormControlLabel
                    value="other"
                    control={<Radio />}
                    label="Other"
                  />
                </RadioGroup>
              </Grid>
              <Grid size={12}>
                <Typography variant="h6">
                  How many direct reports do you have:
                </Typography>
                <RadioGroup
                  value={directors}
                  onChange={handleProfileForm}
                  name="directors"
                  row
                  fullWidth
                  sx={{ justifyContent: "space-between", padding: "0px 10px" }}
                >
                  <FormControlLabel value="0" control={<Radio />} label="0" />
                  <FormControlLabel
                    value="1to5"
                    control={<Radio />}
                    label="1 to 5"
                  />
                  <FormControlLabel
                    value="6to9"
                    control={<Radio />}
                    label="6 to 9"
                  />
                  <FormControlLabel
                    value="more10"
                    control={<Radio />}
                    label="10+"
                  />
                </RadioGroup>
              </Grid>
              <Grid size={12}>
                <Typography variant="h6" color="secondary">
                  Optional demographic questions used for research purposes
                  only:
                </Typography>
              </Grid>
              <Grid size={12}>
                <Typography variant="h6">
                  Please indicate your birth year range
                </Typography>
                <RadioGroup
                  value={yearRange}
                  onChange={handleProfileForm}
                  name="yearRange"
                  row
                  fullWidth
                  sx={{ justifyContent: "space-between", padding: "0px 10px" }}
                >
                  <FormControlLabel
                    value="later1996"
                    control={<Radio />}
                    label="1996 or later"
                  />
                  <FormControlLabel
                    value="1977to1995"
                    control={<Radio />}
                    label="1977-1995"
                  />
                  <FormControlLabel
                    value="1965to1976"
                    control={<Radio />}
                    label="1965-1976"
                  />
                  <FormControlLabel
                    value="1946to1964"
                    control={<Radio />}
                    label="1946-1964"
                  />
                  <FormControlLabel
                    value="before1945"
                    control={<Radio />}
                    label="1945 or before"
                  />
                </RadioGroup>
              </Grid>
              <Grid size={12}>
                <Typography variant="h6">
                  Please indicate your gender
                </Typography>
                <RadioGroup
                  value={gender}
                  onChange={handleProfileForm}
                  name="gender"
                  row
                  fullWidth
                  sx={{ justifyContent: "space-between", padding: "0px 10px" }}
                >
                  <FormControlLabel
                    value="female"
                    control={<Radio />}
                    label="Female"
                  />
                  <FormControlLabel
                    value="male"
                    control={<Radio />}
                    label="Male"
                  />
                  <FormControlLabel
                    value="trans"
                    control={<Radio />}
                    label="Transgender"
                  />
                  <FormControlLabel
                    value="nonBinary"
                    control={<Radio />}
                    label="Non-binary"
                  />
                  <FormControlLabel
                    value="notListed"
                    control={<Radio />}
                    label="Not Listed"
                  />
                  <FormControlLabel
                    value="notAnswer"
                    control={<Radio />}
                    label="Prefer not to answer"
                  />
                </RadioGroup>
              </Grid>
              {error?.msg && (
                <Grid size={12}>
                  <Alert
                    sx={{ marginTop: "20px" }}
                    severity={error.success ? "success" : "error"}
                  >
                    {error.msg}
                  </Alert>
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </form>
    </Container>
  );
};

export default DisplayProfileForm;
